import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Edit2, Check, Plus } from 'lucide-react';
import { Participant, Availability } from '../../types';
import { avatarInitials } from '../../utils/avatar';
import { EmojiNameInput } from '../UI/EmojiNameInput';

interface ParticipantsCardProps {
  participants: Participant[];
  participantName: string | null;
  availability: Availability[];
  onRename: (newName: string) => Promise<void>;
  onInvite: () => void;
}

export function ParticipantsCard({
  participants,
  participantName,
  availability,
  onRename,
  onInvite,
}: ParticipantsCardProps) {
  const { t } = useTranslation();
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');

  const respondedNames = new Set(
    availability.filter(a => a.available).map(a => a.participant)
  );
  const waitingCount = participants.filter(p => !respondedNames.has(p.name)).length;
  const allResponded = waitingCount === 0;

  const handleEditStart = () => {
    setNewName(participantName || '');
    setIsEditingName(true);
  };

  const handleEditSave = async () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== participantName) {
      await onRename(trimmed);
    }
    setIsEditingName(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    }
    if (e.key === 'Escape') {
      setIsEditingName(false);
    }
  };

  return (
    <section className="mb-2 rounded-3xl border border-room-border bg-room-surface px-5 pb-2 pt-[18px]">
      <div className="mb-1.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-room-text-dim" />
          <h3 className="text-[15.5px] font-bold">
            {t('room.participants')} ({participants.length})
          </h3>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold ${
            allResponded
              ? 'bg-room-amber-soft text-room-amber'
              : 'bg-room-amber-soft text-room-amber'
          }`}
        >
          {allResponded
            ? t('participants.allResponded', { defaultValue: 'All responded' })
            : t('participants.waitingOn', {
                count: waitingCount,
                defaultValue: 'Waiting on {{count}}',
              })}
        </span>
      </div>

      <div className="flex flex-col">
        {participants.map(participant => {
          const isYou = participant.name === participantName;
          const isActive = Date.now() - participant.last_seen * 1000 < 5 * 60 * 1000;
          const hasResponded = respondedNames.has(participant.name);

          return (
            <div
              key={participant.name}
              className="flex items-center gap-3 border-t border-room-border py-3 first:border-t-0"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1E8F7C] to-[#123832] text-[12.5px] font-extrabold text-[#EAFBF7]">
                {avatarInitials(participant.name)}
              </div>
              <div className="min-w-0 flex-1 text-left">
                {isYou && isEditingName ? (
                  <EmojiNameInput
                    value={newName}
                    onChange={setNewName}
                    onBlur={handleEditSave}
                    onKeyDown={handleKeyDown}
                    size="sm"
                    maxLength={24}
                    autoFocus
                  />
                ) : (
                  <span className="text-[14.5px] font-bold">{participant.name}</span>
                )}
                <span className="mt-0.5 block text-[11.5px] text-room-text-faint">
                  {isYou
                    ? `${t('common.you')} · ${isActive ? t('room.activeNow') : t('room.lastSeenRecently')}`
                    : hasResponded
                      ? t('participants.responded', { defaultValue: 'Responded' })
                      : t('room.lastSeenRecently')}
                </span>
              </div>
              {isYou ? (
                <button
                  type="button"
                  onClick={isEditingName ? handleEditSave : handleEditStart}
                  aria-label={t('room.editName')}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[11px] border-none bg-transparent text-room-text-dim hover:bg-room-surface-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-room-accent"
                >
                  {isEditingName ? <Check size={17} strokeWidth={2.5} /> : <Edit2 size={17} />}
                </button>
              ) : null}
            </div>
          );
        })}

        <button
          type="button"
          onClick={onInvite}
          className="invite-row flex w-full cursor-pointer items-center gap-3 rounded-[10px] border-none bg-transparent py-3 text-left hover:bg-room-surface-2"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[1.5px] border-dashed border-room-border-strong bg-transparent text-room-accent">
            <Plus size={18} strokeWidth={2.3} />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-[14.5px] font-bold">
              {t('participants.invitePeople', { defaultValue: 'Invite people' })}
            </span>
            <span className="mt-0.5 block text-[11.5px] text-room-text-faint">
              {t('participants.inviteHint', { defaultValue: 'Share a link to join this plan' })}
            </span>
          </div>
        </button>
      </div>
    </section>
  );
}
